import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import AssessmentCta, { BetaPill } from "./AssessmentCta";

const SHOWN_KEY = "tlp-exit-intent-shown";
const WEEK = 7 * 24 * 3600 * 1000;
const HIDE_PATHS = ["/assessment", "/payment"];

const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (HIDE_PATHS.some((p) => pathname.startsWith(p))) return undefined;
    if (window.innerWidth < 768) return undefined; // desktop only, never on mobile
    let shown = 0;
    try { shown = Number(localStorage.getItem(SHOWN_KEY) || 0); } catch { /* noop */ }
    if (shown && Date.now() - shown < WEEK) return undefined;

    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) {
        setOpen(true);
        try { localStorage.setItem(SHOWN_KEY, String(Date.now())); } catch { /* noop */ }
        document.removeEventListener("mouseout", onMouseOut);
      }
    };
    const timer = setTimeout(() => document.addEventListener("mouseout", onMouseOut), 8000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [pathname]);

  /* never stay open over a page where the modal is hidden (e.g. after the
     CTA navigates to /assessment) */
  useEffect(() => {
    if (open && HIDE_PATHS.some((p) => pathname.startsWith(p))) setOpen(false);
  }, [pathname, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-void/80 p-6 backdrop-blur-md"
          data-testid="exit-intent-modal"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-3xl border border-white/10 bg-surface p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" aria-label="Close" onClick={() => setOpen(false)} data-testid="exit-intent-close"
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white">
              <X className="h-4 w-4" />
            </button>
            <p className="flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-cyan-300">
              Before you go <BetaPill />
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold leading-snug">Let's check your current Lifespan Markers</h2>
            <p className="mt-3 text-sm font-light leading-relaxed text-fog">
              A free Level 0 baseline of your body and mind, compared with the world average.
            </p>
            <div onClick={() => setOpen(false)}>
              <AssessmentCta src="exit" variant="primary" className="mt-6 w-full" testId="exit-intent-cta" />
            </div>
            <p className="mt-4 text-xs text-white/40">Free | 4-5 min | No login | Private</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentModal;
